/**
 * Top-level routes.
 *
 *   /login                  public — kicks PKCE
 *   /auth/callback          public — exchanges code → tokens
 *   /* (everything else)    auth-guarded; renders inside the Shell
 *
 * Shell is the shadcn-based chrome (R22.5). It composes the routed pages
 * via AppRoutes so all auth-required surfaces share the same header +
 * sidebar without each page having to re-import them.
 */
import { Route, Routes, Navigate } from 'react-router-dom';

import { AuthGuard } from './auth/AuthGuard.js';
import { Shell } from './components/shell.js';
import GpuHostsPage from './pages/gpu/GpuHostsPage.js';
import GpuReadinessPage from './pages/gpu/GpuReadinessPage.js';
import UsersPage from './pages/users/UsersPage.js';
import LoRAsPage from './pages/loras/LoRAsPage.js';
import { FounderGuard } from './auth/FounderGuard.js';
import LoginPage from './pages/auth/LoginPage.js';
import CallbackPage from './pages/auth/CallbackPage.js';

function Placeholder({ name }: { name: string }) {
  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold tracking-tight">{name}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Real surface lands in Phase B-Admin Steps A8-A21. See{' '}
        <a
          className="underline underline-offset-2 hover:text-foreground"
          href="https://github.com/proximaxleslie/tacosai-v5/blob/main/docs/28-admin-app-design.md"
        >
          docs/28-admin-app-design.md
        </a>
        .
      </p>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/gpu" replace />} />
      <Route path="/users" element={<UsersPage />} />
      <Route path="/payments" element={<Placeholder name="Payments" />} />
      <Route path="/gpu" element={<GpuHostsPage />} />
      <Route path="/gpu/readiness" element={<GpuReadinessPage />} />
      <Route
        path="/loras"
        element={
          <FounderGuard>
            <LoRAsPage />
          </FounderGuard>
        }
      />
      <Route path="/workflows" element={<Placeholder name="ComfyUI templates" />} />
      <Route path="/audit" element={<Placeholder name="Audit log" />} />
      <Route path="/config" element={<Placeholder name="System config" />} />
      <Route path="/moderation" element={<Placeholder name="Moderation" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route
        path="*"
        element={
          <AuthGuard>
            <Shell>
              <AppRoutes />
            </Shell>
          </AuthGuard>
        }
      />
    </Routes>
  );
}
